import { GET_TEXT_CANCEL_POLICIES, TIME_UNITS } from '../config/cancelPolicies';
import moment from 'moment';
import { contentGetter } from '@synergycms_core/content/i18n';
import { DISCOUNT_FLEX_RATE } from '../config/constants';
import { List, Map } from 'immutable';

const percentOf = (value, percent, subtract) => {
  const percentValue = value * percent / 100;
  return subtract ? value - percentValue : percentValue;
};
const getPenaltyAmount = (penalty, stanceTotal, avgNightPrice) => {
  return penalty.get('type') === 'percentage' ?
    percentOf(stanceTotal, penalty.get('value')) :
    avgNightPrice * penalty.get('value');
};

const getTextRelease = (release, showAs) => {
  let textRelease;  

    switch (showAs) {
      case 'weeks':
        textRelease = release / 24 / 7;
        break;
      case 'days':
        textRelease = release / 24;
        break;
      case 'hours':
      default:
        textRelease = release;
    }
  

  return textRelease;
};

export const getPenalties = (reservation, isProperty, pms = null, lang = null, managementGroupID = null) => {
  const checkin = moment(reservation.get('checkin').split('T')[0]);
  const checkout = moment(reservation.get('checkout').split('T')[0]);
  const stayLength = checkout.diff(checkin, 'd');
  const stanceTotal = reservation.get('total_price_tax');
  const discounts = reservation.get('discounts', List());
  
  const daysDiff = checkin.diff(moment(), 'd', true);
  const serviceNR = reservation.get('services', List()).find(service =>
    service.get('penalties', List()).some(penalty =>
      penalty.get('value') === 100 && penalty.get('release') * 24 >= daysDiff
    )
  );
  const resultado = reservation.get('penalties').reduce((arrayPenalties, penalty) => {

    const reservationRates = reservation.get('rates', List());
    const rateRestrictive = reservationRates.find(r => !r.get('flexible', false)) || reservationRates.find(r => r.get('flexible', false)) || Map();
    const penaltyObj =parsePenalty(
      penalty,
      checkin,
      stanceTotal,
      stayLength,
      !reservation.get('blocks').some(b => b.get('refundable') === false),
      discounts,
      rateRestrictive,
      isProperty,
      !!serviceNR,
      pms,
      lang,
      managementGroupID
    );
    
    arrayPenalties.push(penaltyObj);

    arrayPenalties = arrayPenalties.filter((penalty, index, penalties) => {
      let penaltyAmount = penalty.amount;

      //Si una política te menor o igual penalització que l'anterior es una política innecessaria
      if (index !== 0) {
        let prevPenalty = penalties[index - 1];
        // Si tienen el mismo release
        if (penalty.release === prevPenalty.release) {
          // Descarta el penalty actual si la política anterior no tiene same_day y la actual sí lo tiene.
          if (!prevPenalty.same_day && penalty.same_day) return false;

          // Si ambas políticas tienen same_day y el mismo release
          if (penalty.same_day && prevPenalty.same_day) {
              // Si la política actual es SAME_DAY (ya hemos dado prioridad a LAST_DAY en la ordenación), descartarla.
              if (penalty.same_day === "SAME_DAY") return false;
          }
        }

        return +penalty.value >= 0 && penaltyAmount > prevPenalty.amount;

      }
        // Una política activa es aquella en que el release ja s'està complint,
      // entre aquestes, ens quedarem la de major penalització
      else if (penalties.length > 1 && typeof penalties[index + 1] !== 'undefined' && penalty.applyPenaltyRelease) {
        let nextPenaltyAmount = penalties[index + 1].applyPenaltyRelease ? 
                                penalties[index + 1].penaltyAmount : 
                                0;
        return +penalty.value >= 0 && penalty.penaltyAmount > nextPenaltyAmount;
      }
      
      return true;
      
    });

    return arrayPenalties;
  }, []);
  return resultado;
};


/*
  Entradas:
  - penalty: Objeto con información sobre la penalización.
  - checkin: Fecha de check-in.
  - stanceTotal: Precio total de la estancia.
  - stayLength: Duración de la estancia en noches.
  - blockRefundable: Valor booleano que indica si se bloquea el reembolso (opcional, valor predeterminado true).
  - discounts: Descuentos disponibles.
  - rate: Tarifa de la reserva.
  - isProperty: Valor booleano que indica si la propiedad es propia.
  - hasNRService: Valor booleano que indica si se ofrece un servicio NR (opcional, valor predeterminado false).
  - pms: Sistema de gestión de propiedades (opcional).
  - lang: Idioma (opcional).
  - managementGroupID: ID del grupo de gestión (opcional).

  Proceso:
  1. Inicializa constantes y variables locales, como CANCEL_POLICIES, avgNightPrice, y otras.
  2. Extrae propiedades relevantes del objeto "penalty" para facilitar su acceso.
  3. Calcula "adjustedCheckin" basado en el valor de "same_day" y la función "adjustCheckinByRelease".
  4. Determina si se debe aplicar la penalización en función de "adjustedCheckin".
  5. Inicializa variables de control para "refundable", "fullFree", y "penaltyText".
  6. Evalúa si la reserva es reembolsable o no y ajusta la variable "refundable" en consecuencia.
  7. Maneja casos especiales de "pms" como "FlexMyRoom".
  8. Busca descuentos flexibles y ajusta "refundable" en función de ellos.
  9. Define una función "getPenaltyObj" para crear objetos de penalización con texto y atributos.
  10. Evalúa el caso de penalización "NO REEMBOLSABLE" y retorna un objeto de penalización correspondiente.
  11. Evalúa el caso de penalización "Reembolsable" y retorna un objeto de penalización correspondiente.
  12. Realiza ajustes específicos para casos de penalización "SAME_DAY" y "LAST_DAY".
  13. Formatea la salida del objeto de penalización según el tipo y otros atributos.

  Salida:
  - Objeto de penalización con texto y atributos relevantes.
*/
export const parsePenalty = (penalty, checkin, stanceTotal, stayLength, blockRefundable = true, discounts, rate, isProperty, hasNRService = false, pms = null, lang = null, managementGroupID = null) => {
  
  const CANCEL_POLICIES = GET_TEXT_CANCEL_POLICIES(pms);
  const avgNightPrice = stanceTotal / stayLength;

  const { release, value, type, show_as, same_day } = penalty.toJS();

  //Calcular "adjustedCheckin" basado en "same_day"
  let adjustedCheckin;
  let daysRelease = 0;
  switch (same_day) {
      case "SAME_DAY":
          adjustedCheckin = adjustCheckinByRelease(checkin, release, 0, true);
          break;
      case "LAST_DAY":
          adjustedCheckin = adjustCheckinByRelease(checkin, release, 1, true);
          break;
      default:
          daysRelease = release / 24;
          adjustedCheckin = checkin.clone().subtract(daysRelease, 'd');
          break;
  }

  //¿Se debe aplicar la penalización? Definimos el valor de la variable refundable true: Si se debe aplicar la penalización, false: Si no se debe aplicar la penalización
  const applyPenaltyRelease = shouldApplyPenalty(adjustedCheckin);

  let refundable = blockRefundable;
  let fullFree = type === 'percentage' && value === 0 && release === 23976 && show_as === 'days';
  let penaltyText = '';

  if (refundable && applyPenaltyRelease && ((type === 'percentage' && !(value < 100)) || (type === 'days' && !(value < stayLength)))) {
    refundable = false;
  } else if (!refundable && fullFree) {
    refundable = true;
  }


  // flexmyroom | secure cancellation (forces all rates to be NR)
  if (pms === 'FlexMyRoom') {
    refundable = false;
  }


  const flexDiscount = discounts.find(d => (d.get('acronym', '') || '').includes(DISCOUNT_FLEX_RATE)) || false;
  let flexDiscountNew = discounts.find(d => d.get('flexible', false)) || false;
  if(!flexDiscountNew) {
    flexDiscountNew = rate.get('flexible', false) && rate;
  }

  const getPenaltyObj = (text, props = {}) => {
    return {
      text,
      ...props
    };
  };

  //  Caso: NO REEMBOLSABLE
  if (!refundable) {
    // Caso: No reembolsable - ya sea una propiedad o sin descuento flexible.
    if (isProperty || !flexDiscount) {
      penaltyText = flexDiscount ? CANCEL_POLICIES['flexible_rate'] : CANCEL_POLICIES.nr;
      return getPenaltyObj(penaltyText, {
        nr: true,
        daysBefore: flexDiscount ? flexDiscount.get('acronym').split(DISCOUNT_FLEX_RATE)[1] : 0,
        flexible: !!flexDiscount,
        pms,
        lang
      });
    }
    // Caso: Descuento flexible para hoteles que no son propiedad y no tienen servicio NR.
    else if (!isProperty && flexDiscount && !hasNRService) {
      const daysRelease = flexDiscount ? flexDiscount.get('acronym').split(DISCOUNT_FLEX_RATE)[1] : 0;
      const releaseDay = checkin.clone().subtract(daysRelease, 'd');
      const daysDiff = releaseDay.diff(moment(), 'd', true);
      const applyPenaltyRelease = daysDiff <= 0;
      penaltyText = applyPenaltyRelease ? CANCEL_POLICIES.nr : CANCEL_POLICIES['ref_percentage'];
      
      return getPenaltyObj(penaltyText,{
        nr: applyPenaltyRelease,
        flexible: false,
        anticipation: getTextRelease(daysRelease * 24, 'days'),
        daysBefore: 0,
        applyPenaltyRelease: applyPenaltyRelease && !fullFree,
        penaltyAmount: getPenaltyAmount(penalty, stanceTotal, avgNightPrice),
        release: daysRelease,
        time_unit: TIME_UNITS['days'],
        value: 100
      });

    }
  } 
  // Caso: Reembolsable
  else {
    const timeUnit = TIME_UNITS[show_as];
    // Caso: Reembolsable

    /*
    ¿Es un caso específico de penalización?
    |    V
    |    Si lo es:
    |    |--- Crea un objeto de penalización vacío
    |    |--- Asigna el texto correspondiente a "refundable_free" al atributo "text" del objeto
    |    |--- Agrega los atributos de la penalización al objeto
    |    |--- Retorna el objeto de penalización completo
    */
    if (isSpecificPenaltyCase(type, value, release, show_as)) {
      penaltyText = CANCEL_POLICIES['refundable_free'];
      return getPenaltyObj(penaltyText, {
        nr: false,
        anticipation: getTextRelease(daysRelease * 24, 'days'),
        applyPenaltyRelease: applyPenaltyRelease && !fullFree,
        release: release,
        showAs: show_as,
        type: type,
        value: value
      });
    }
    // Caso: DESCUENTO FLEXIBLE para todos.
    if (isFlexibleDiscount(refundable, flexDiscountNew, hasNRService)) {
      const daysBefore = flexDiscountNew.get('flexible_release', 0);
      penaltyText = CANCEL_POLICIES['flexible_rate'];
      return getPenaltyObj(penaltyText, {
        nr: true,
        daysBefore: daysBefore,
        flexible: true
      });
    }
    
    let { dateToShow, hourToShow } = getDateAndHourToShow(checkin.clone(), daysRelease);

    let penaltyTextKey = determinePenaltyKey(dateToShow, hourToShow, type, managementGroupID, isProperty, applyPenaltyRelease);

    if (flexDiscount && isProperty) {
        penaltyTextKey = "flexible_rate";
    }

    //  Realiza ajustes específicos para casos de penalización "SAME_DAY" y "LAST_DAY"
    if (isSameDayPenalty(penalty)) {
      
      penaltyTextKey = `ref_${type}_same_day`;
      
      hourToShow = moment().startOf("day").add(release).subtract(1, 'minutes').format('HH:mm');

    } else if (isLastDayPenalty(penalty)) {
      let adjustedCheckin = adjustCheckinDateByRelease(checkin, release);

      let datos = getDateAndHourToShow(adjustedCheckin, 1, true);
      dateToShow = datos.dateToShow;
      hourToShow = datos.hourToShow;
      penaltyTextKey = determinePenaltyKey(dateToShow, hourToShow, type, managementGroupID, isProperty, applyPenaltyRelease);

    } else if (release < 24){
        adjustedCheckin = checkin.clone().subtract(release, 'hours').subtract(1, 'minutes');
        dateToShow = formatDateToShow(adjustedCheckin);
        hourToShow = formatHourToShow(adjustedCheckin);
    }

    penaltyText = CANCEL_POLICIES[penaltyTextKey];
    const penaltyAttributes = {
      nr: false,
      flexible: !!flexDiscount,
      anticipation: isSameDayPenalty(penalty) || isLastDayPenalty(penalty) ? hourToShow : getTextRelease(release, show_as),
      daysBefore: flexDiscount ? flexDiscount.get('acronym').split(DISCOUNT_FLEX_RATE)[1] : 0,
      applyPenaltyRelease: applyPenaltyRelease && !fullFree,
      penalty: penalty,
      stanceTotal: stanceTotal,
      avgNightPrice: avgNightPrice,
      penaltyAmount: getPenaltyAmount(penalty, stanceTotal, avgNightPrice),
      release: release,
      time_unit: timeUnit,
      value: value,
      hours: hourToShow,
      date: dateToShow
    };

    // Agregamos 'nights' o 'value' dependiendo del tipo.
    const i18n = contentGetter();
    if (type === 'days') {
      penaltyAttributes.nights = value;
      penaltyAttributes.nights_text = i18n.t('_%noche%_', value !== 1);
    } else {
      penaltyAttributes.value = value;
    }
    
    return getPenaltyObj(penaltyText, penaltyAttributes);
  }
};



export const validateRefReservation = reservation => {
  return reservation.find();
};




/**
 * Ajusta la fecha de check-in proporcionada según la información de lanzamiento.
 *
 * @param {Object} checkin - Objeto moment.js que representa la fecha de check-in.
 * @param {number|Object} release - El lanzamiento puede ser un número (representando horas) o una marca de tiempo (cuando isTimestamp es verdadero).
 * @param {number} daysToSubtract - Número de días a restar de la fecha de check-in.
 * @param {boolean} isTimestamp - Bandera para indicar si el lanzamiento es una marca de tiempo (verdadero) o un número (falso).
 * @return {Object} - Devuelve un objeto moment.js de la fecha de check-in ajustada.
 */
function adjustCheckinByRelease(checkin, release, daysToSubtract = 0, isTimestamp = false) {
  const checkinClone = checkin.clone().subtract(daysToSubtract, 'days');

  let releaseDuration;
  if (isTimestamp) {
    let time = moment().startOf("day").add(release);
    const horas = time.hours();
    let minutos = time.minutes();
    minutos = minutos < 10 ? "0" + minutos : minutos;
    let horaDecimal = horas + ":" + minutos;
    const releaseMoment = moment(horaDecimal, 'HH:mm');
    releaseDuration = moment.duration(releaseMoment.diff(moment(releaseMoment).startOf('day')));
  } else {
      releaseDuration = moment.duration(release, 'hours');
  }

  checkinClone.set({
      hour: releaseDuration.hours(),
      minute: releaseDuration.minutes(),
      second: releaseDuration.seconds()
  });
  return checkinClone;
}

function shouldApplyPenalty(adjustedDate) {
  const daysDiff = adjustedDate.diff(moment(), 'd', true);
  const hoursDiff = adjustedDate.diff(moment(), 'h', true);
  return daysDiff <= 0 && hoursDiff <= 0;
}

/**
 * Determina la key del texto de la penalización basada en la fecha, hora y otros criterios.
 *
 * @param {string} date - La fecha de referencia en formato de cadena (ejemplo: '2023-10-27').
 * @param {string} hour - La hora de referencia en formato de cadena (ejemplo: '15:30').
 * @param {string} type - El tipo de penalización (ejemplo: 'percentage' o 'days').
 * @param {number} managementGroupID - El ID del grupo hotelero.
 * @param {boolean} isProperty - Indica si el hotel es una propiedad.
 * @param {boolean} applyPenaltyRelease - Indica si ya se ha decidido que se debe aplicar la penalización.
 * @return {string} - Devuelve la clave de la penalización determinada.
 */
function determinePenaltyKey(date, hour, type, managementGroupID, isProperty, applyPenaltyRelease) {
  let penaltyKey = `ref_${type}`;

  if (managementGroupID === 9 || isProperty) { // 9 = Grupo Hoteles YitHotels
      penaltyKey = getRefPenaltyKeyForDate(date, hour, type);

  }
  if (applyPenaltyRelease) {
      penaltyKey = `past_release_${penaltyKey}`;
  }
  return penaltyKey;
}

/**
 * Toma una fecha de check-in (checkinDate) y un valor de tiempo de lanzamiento en formato de marca de tiempo (releaseTimestamp)
 * y luego configura la hora, los minutos y los segundos de la fecha de check-in para que coincidan con los de la marca de tiempo.
 * En esencia, ajusta la fecha de check-in a la hora exacta definida por el valor del timestamp. Por ejemplo, si releaseTimestamp
 * representa las 18:30:00, la función configurará la fecha de check-in para que tenga la misma hora, minutos y segundos.
 * @param {Object} checkinDate - Objeto moment.js que representa la fecha de check-in.
 * @param {number} releaseTimestamp - El valor de tiempo de lanzamiento en formato de marca de tiempo (timestamp).
 * @returns {Object} - Devuelve un objeto moment.js que representa la fecha de check-in ajustada con la hora y los minutos del valor de tiempo de lanzamiento.
 */
function adjustCheckinDateByRelease(checkinDate, releaseTimestamp) {
  let checkinClone = checkinDate.clone();
  let releaseTime = moment().startOf("day").add(releaseTimestamp);
  checkinClone.hours(releaseTime.hours());
  checkinClone.minutes(releaseTime.minutes());
  checkinClone.seconds(releaseTime.seconds());
  return checkinClone;
}

function isSpecificPenaltyCase(type, value, release, show_as) {
  return type === 'percentage' && value === 0 && release === 23976 && show_as === 'days';
}

function isFlexibleDiscount(refundable, flexDiscountNew, hasNRService) {
  return refundable && flexDiscountNew && !hasNRService && flexDiscountNew.get('flexible_release', 0) > 0;
}

/**
 * Obtiene la clave de penalización de referencia basada en la fecha y hora especificadas.
 *
 * @param {string} dateToShow - La fecha de referencia en formato de cadena (ejemplo: '27/10/2023').
 * @param {string} hourToShow - La hora de referencia en formato de cadena (ejemplo: '15:30').
 * @param {string} type - El tipo de penalización (ejemplo: 'percentage' o 'days').
 * @return {string} - Devuelve la clave de penalización de referencia correspondiente.
 */
function getRefPenaltyKeyForDate(dateToShow, hourToShow, type) {
  const today = moment().toDate();
  const dateParts = dateToShow.split("/");
  const constDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]} ${hourToShow}`;
  const fullDate = new Date(constDate);

  return fullDate.getTime() <= today.getTime() ? `ref_${type}` : `ref_${type}_group`;
}


/**
 * Ajusta una fecha de check-in restando un número de días especificado.
 *
 * @param {string} stringDateCheckin - La fecha de check-in en formato 'DD/MM/YYYY HH:mm:ss'.
 * @param {number} daysRelease - La cantidad de días a restar a la fecha de check-in.
 * @returns {string} - La fecha de check-in ajustada en el mismo formato de entrada.
 */
function adjustDateByReleaseDays(stringDateCheckin, daysRelease) {
  // Interpreta la fecha
  let mDateTime = moment(stringDateCheckin, 'DD/MM/YYYY HH:mm:ss');
  // Resta un día y un minuto
  let mDateMaxCancellation = mDateTime.subtract(daysRelease, 'days').subtract(1, 'minutes');

  return mDateMaxCancellation;
}


function isSameDayPenalty(penalty) {
  return penalty.get("same_day", false) && penalty.get("same_day") === "SAME_DAY";
}

function isLastDayPenalty(penalty) {
  return penalty.get("same_day", false) && penalty.get("same_day") === "LAST_DAY";
}

function formatDateToShow(date) {
  return moment(date).format('DD/MM/YYYY');
}

function formatHourToShow(date) {
  return moment(date).format('HH:mm');
}

function getDateAndHourToShow(checkin, daysRelease, ajustedHours = false) {

  let date = adjustDateByReleaseDays(!ajustedHours ? checkin.format('DD/MM/YYYY') +" 00:00:00" : checkin, daysRelease);
  
  const dateToShow = formatDateToShow(date);
  const hourToShow = formatHourToShow(date);
  return { dateToShow, hourToShow };
}